import { Dialog, DialogContent, Box, Typography } from '@mui/material';
import { TeammateDetails } from '@/types/teammates';
import LockTeammateForm from '../forms/LockTeammateForm';
import UnlockTeammateForm from '../forms/UnlockTeammateForm';
import {
  useLockTeammate,
  useUnlockTeammate,
} from '@/hooks/requests/useTeammates';

type ToggleTeammateLockModalProps = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  teammate: Pick<TeammateDetails, 'uuid'> & {
    fullName: string;
    isLocked: boolean;
  };
  scrollToTop: (behavior?: ScrollBehavior) => void;
};

export default function ToggleTeammateLockModal({
  open,
  setOpen,
  teammate,
  scrollToTop,
}: ToggleTeammateLockModalProps) {
  const { lockTeammate, isLockTeammatePending } = useLockTeammate(
    teammate.uuid,
  );
  const { unlockTeammate, isUnlockTeammatePending } = useUnlockTeammate(
    teammate.uuid,
  );
  const handleClose = () => {
    setOpen(false);
  };

  const getLockTeammateForm = () => {
    return (
      <>
        <Typography variant="body1" component="p" mb={2}>
          Are you sure you want to lock {teammate.fullName}?
          <br />
          <br />
          This will disable the user's ability to log in.
        </Typography>
        <LockTeammateForm
          teammateFullName={teammate.fullName}
          lockTeammate={lockTeammate}
          isLockTeammatePending={isLockTeammatePending}
          onSuccess={() => {
            setOpen(false);
            scrollToTop();
          }}
          onCancel={handleClose}
        />
      </>
    );
  };

  const getUnlockTeammateForm = () => {
    return (
      <>
        <Typography variant="body1" component="p" mb={2}>
          Are you sure you want to unlock {teammate.fullName}?
          <br />
          <br />
          This will enable the user's ability to log in.
        </Typography>
        <UnlockTeammateForm
          teammateFullName={teammate.fullName}
          unlockTeammate={unlockTeammate}
          isUnlockTeammatePending={isUnlockTeammatePending}
          onSuccess={() => {
            setOpen(false);
            scrollToTop();
          }}
          onCancel={handleClose}
        />
      </>
    );
  };

  return (
    <Dialog fullWidth open={open} onClose={handleClose}>
      <DialogContent>
        <Box>
          <Typography variant="h6" component="h2">
            {teammate.isLocked ? 'Unlock Teammate?' : 'Lock Teammate?'}
          </Typography>
          <Box sx={{ mt: 2 }}>
            {teammate.isLocked
              ? getUnlockTeammateForm()
              : getLockTeammateForm()}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
